import { Checkbox, FormControl, FormControlLabel, Stack, Typography } from '@mui/material'
import { DatePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import type { DateTime } from 'luxon'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ZONED_TODAY } from '@/constants/dateTime'
import { renderDuration } from '@/features/site/components/availability/restriction/form/renderDuration'
import type { RestrictionForm } from '@/features/site/types/restrictionForm'

export const ResourcelevelRestrictionNonRepeatingDatesSection = ({ timezone }: { timezone: string }) => {
  const { t, i18n } = useTranslation()
  const { getValues, control, setValue, trigger } = useFormContext<RestrictionForm>()
  const { id, type, allDay, startDate, startTime, endDate, endTime } = getValues()

  function isDateSelectorDisabled(date: DateTime) {
    const now = ZONED_TODAY(timezone)
    if (id) {
      return now > date
    }
  }

  const handleFieldChange = (fieldName: keyof RestrictionForm) => (value: any) => {
    setValue(fieldName, value)
    trigger(['startDate', 'startTime', 'endDate', 'endTime'])
  }

  const hasCompleteAndValidDates =
    startDate &&
    endDate &&
    startTime &&
    endTime &&
    startDate.isValid &&
    endDate.isValid &&
    startTime.isValid &&
    endTime.isValid

  return (
    <>
      <Stack direction="row" spacing={1}>
        <FormControl>
          <Controller
            control={control}
            name="startDate"
            render={({ field, fieldState }) => (
              <LocalizationProvider adapterLocale={i18n.language} dateAdapter={AdapterLuxon}>
                <DatePicker
                  disabled={isDateSelectorDisabled(startDate)}
                  enableAccessibleFieldDOMStructure={false}
                  label={t('customer_details.tabs.availability.common.form.dates_section.start_date')}
                  minDate={ZONED_TODAY(timezone)}
                  {...field}
                  format="dd MMM yyyy"
                  slotProps={{
                    textField: {
                      error: !!fieldState.error,
                      helperText: fieldState.error?.message,
                    },
                  }}
                  views={['day', 'month', 'year']}
                  onChange={(value) => {
                    field.onChange(value)
                    handleFieldChange('startDate')(value)
                  }}
                />
              </LocalizationProvider>
            )}
          />
        </FormControl>
        <FormControl>
          <Controller
            control={control}
            name="startTime"
            render={({ field, fieldState }) => (
              <LocalizationProvider adapterLocale={i18n.language} dateAdapter={AdapterLuxon}>
                <TimePicker
                  disabled={allDay}
                  enableAccessibleFieldDOMStructure={false}
                  label={t('customer_details.tabs.availability.common.form.dates_section.start_time')}
                  {...field}
                  ampm={false}
                  slotProps={{
                    textField: {
                      error: !!fieldState.error,
                      helperText: fieldState.error?.message,
                    },
                  }}
                  timeSteps={{
                    minutes: 1,
                  }}
                  views={['hours', 'minutes']}
                  onChange={(value) => {
                    field.onChange(value)
                    handleFieldChange('startTime')(value)
                  }}
                />
              </LocalizationProvider>
            )}
          />
        </FormControl>
        <FormControl>
          <Controller
            control={control}
            name="endDate"
            render={({ field, fieldState }) => (
              <LocalizationProvider adapterLocale={i18n.language} dateAdapter={AdapterLuxon}>
                <DatePicker
                  disabled={isDateSelectorDisabled(endDate)}
                  enableAccessibleFieldDOMStructure={false}
                  label={t('customer_details.tabs.availability.common.form.dates_section.end_date')}
                  {...field}
                  format="dd MMM yyyy"
                  slotProps={{
                    textField: {
                      error: !!fieldState.error,
                      helperText: fieldState.error?.message,
                    },
                  }}
                  views={['day', 'month', 'year']}
                  onChange={(value) => {
                    field.onChange(value)
                    handleFieldChange('endDate')(value)
                  }}
                />
              </LocalizationProvider>
            )}
          />
        </FormControl>
        <FormControl>
          <Controller
            control={control}
            name="endTime"
            render={({ field, fieldState }) => (
              <LocalizationProvider adapterLocale={i18n.language} dateAdapter={AdapterLuxon}>
                <TimePicker
                  disabled={allDay}
                  enableAccessibleFieldDOMStructure={false}
                  label={t('customer_details.tabs.availability.common.form.dates_section.end_time')}
                  {...field}
                  ampm={false}
                  slotProps={{
                    textField: {
                      error: !!fieldState.error,
                      helperText: fieldState.error?.message,
                    },
                  }}
                  timeSteps={{
                    minutes: 1,
                  }}
                  views={['hours', 'minutes']}
                  onChange={(value) => {
                    field.onChange(value)
                    handleFieldChange('endTime')(value)
                  }}
                />
              </LocalizationProvider>
            )}
          />
        </FormControl>
        <FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={allDay}
                onChange={(event) => {
                  setValue('allDay', event.target.checked)
                  if (event.target.checked) {
                    setValue('startTime', ZONED_TODAY(timezone).startOf('day'))
                    setValue('endTime', ZONED_TODAY(timezone).endOf('day').startOf('minute'))
                  }
                }}
              />
            }
            label={t('customer_details.tabs.availability.common.form.dates_section.all_day')}
          />
        </FormControl>
      </Stack>
      <Typography variant="caption">
        {t('customer_details.tabs.availability.common.form.dates_section.duration')}:
        {hasCompleteAndValidDates
          ? renderDuration(type, endDate, endTime, startDate, startTime)
          : t('customer_details.tabs.res_level_unavailability.common.form.dates_section.duration_pending')}
      </Typography>
    </>
  )
}
